import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  Chip,
  Link,
  Divider,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import { StateManager } from '../utilities/stateManager';
import RequestManager from '../utilities/requestManager';
import { StackedDialog } from '../utilities/ModalStackContext';

// Select lenders → preview exactly what each receives → send. Used on the
// credit-app page and on the car modal's Finance tab. Sending sets the credit
// app status to "sent" (server-side).
//
// `headerContent` renders above the lender list — the Finance tab uses it to pick
// which buyer/co-buyer credit app goes out. `prepare` runs before the preview is
// built, so a caller can link the app to a car or co-applicant first.
export default function SendCreditAppDialog({
  open,
  onClose,
  creditAppId,
  onSent,
  title = 'Send Credit App to Lenders',
  headerContent = null,
  prepare = null,
}) {
  const [step, setStep] = React.useState('select'); // 'select' | 'preview'
  const [selected, setSelected] = React.useState([]);
  const [previewing, setPreviewing] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const [extraCc, setExtraCc] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setStep('select'); setSelected([]); setPreview(null);
      setPreviewing(false); setSending(false); setExtraCc('');
    }
  }, [open]);

  // Parse the comma/space/newline-separated extra CC field into a clean list.
  const parseEmails = (str) => (str || '').split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);

  const lenders = StateManager.banks; // already filtered to active; email may be unset

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      if (prepare) await prepare();
      const res = await RequestManager.post({
        function: 'previewCreditApp',
        variables: { creditAppId, banks: selected },
      });
      if (res.error) throw new Error(res.error);
      setPreview(res);
      setStep('preview');
    } catch (e) {
      StateManager.setAlertAndOpen(e.message || 'Could not build preview', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await RequestManager.post({
        function: 'sendCreditApp',
        variables: { creditAppId, banks: selected, sentById: StateManager.userID, extraCc: parseEmails(extraCc) },
      });
      if (res.success === false || res.error) throw new Error(res.error || 'Send failed');
      StateManager.setAlertAndOpen(`Credit application sent to ${(res.banks || []).length} lender(s)`, 'success');
      if (onSent) onSent();
      onClose();
    } catch (e) {
      StateManager.setAlertAndOpen(e.message || 'Error sending credit application', 'error');
    } finally {
      setSending(false);
    }
  };

  // Group the preview's banks by invoice format so each format's invoice is shown once.
  const banksByFormat = {};
  (preview?.banks || []).forEach(b => {
    (banksByFormat[b.invoiceFormat] = banksByFormat[b.invoiceFormat] || []).push(b);
  });

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {step === 'select' && (
          <>
            {headerContent}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose the lenders to send this credit application to.
            </Typography>
            <List dense>
              {lenders.map(bank => {
                const hasEmail = !!bank.data?.email;
                return (
                  <ListItem key={bank.value} disablePadding>
                    <ListItemButton onClick={() => toggle(bank.data.id)} disabled={!hasEmail} dense>
                      <Checkbox edge="start" checked={selected.includes(bank.data.id)} tabIndex={-1} disableRipple />
                      <ListItemText
                        primary={bank.label}
                        secondary={hasEmail
                          ? `${bank.data.email} · ${bank.data.invoiceFormat === 'rollup' ? 'rolled-up' : 'itemized'} bill of sale`
                          : 'No email configured — set one in Lender Management'}
                        secondaryTypographyProps={hasEmail ? undefined : { color: 'error' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}

        {step === 'preview' && preview && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Review what each lender will receive, then send. Sending marks this app as <strong>sent</strong>.
            </Alert>

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Documents</Typography>
            <Box sx={{ mb: 1.5, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {preview.creditAppUrl && (
                <Link href={preview.creditAppUrl} target="_blank" rel="noopener" variant="body2">Credit application PDF</Link>
              )}
              {preview.coAppUrl && (
                <Link href={preview.coAppUrl} target="_blank" rel="noopener" variant="body2">
                  Co-applicant credit application PDF{preview.coApplicantName ? ` (${preview.coApplicantName})` : ''}
                </Link>
              )}
              {!preview.hasInvoice && (
                <Typography variant="body2" color="warning.dark">
                  No bill of sale — link a car with pricing to include one.
                </Typography>
              )}
              {preview.hasInvoice && Object.entries(banksByFormat).map(([fmt, banks]) => (
                <Box key={fmt} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={fmt === 'rollup' ? 'Rolled-up Bill of Sale' : 'Itemized Bill of Sale'} sx={{ fontSize: '0.65rem', height: 18 }} />
                  {banks[0].invoiceUrl && (
                    <Link href={banks[0].invoiceUrl} target="_blank" rel="noopener" variant="body2">preview</Link>
                  )}
                  <Typography variant="caption" color="text.secondary">→ {banks.map(b => b.name).join(', ')}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                {preview.licenseFrontUrl
                  ? <Link href={preview.licenseFrontUrl} target="_blank" rel="noopener" variant="body2">License front</Link>
                  : <Typography variant="caption" color="text.secondary">No license front on file</Typography>}
                {preview.licenseBackUrl
                  ? <Link href={preview.licenseBackUrl} target="_blank" rel="noopener" variant="body2">License back</Link>
                  : <Typography variant="caption" color="text.secondary">No license back on file</Typography>}
              </Box>
              {preview.coApplicantName && (
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  {preview.coLicenseFrontUrl
                    ? <Link href={preview.coLicenseFrontUrl} target="_blank" rel="noopener" variant="body2">Co-applicant license front</Link>
                    : <Typography variant="caption" color="text.secondary">No co-applicant license front on file</Typography>}
                  {preview.coLicenseBackUrl
                    ? <Link href={preview.coLicenseBackUrl} target="_blank" rel="noopener" variant="body2">Co-applicant license back</Link>
                    : <Typography variant="caption" color="text.secondary">No co-applicant license back on file</Typography>}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Recipients</Typography>
            <Box sx={{ mt: 0.5 }}>
              {(preview.banks || []).map(b => (
                <Typography key={b.id} variant="body2">{b.name} — {b.email}</Typography>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Natalie and the deal's sales rep are CC'd automatically — the sales rep is always included, even when they're the one sending.
              Large license photos are compressed, and linked instead of attached if still over the email size limit.
            </Typography>

            <Divider sx={{ my: 1.5 }} />
            <TextField
              fullWidth
              size="small"
              label="Additional CC (optional)"
              placeholder="email@example.com, another@example.com"
              value={extraCc}
              onChange={e => setExtraCc(e.target.value)}
              helperText="Comma-separated. Added to every lender on this send."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending || previewing}>Cancel</Button>
        {step === 'preview' && (
          <Button onClick={() => setStep('select')} disabled={sending}>Back</Button>
        )}
        {step === 'select' && (
          <Button variant="contained" onClick={runPreview} disabled={!creditAppId || selected.length === 0 || previewing}>
            {previewing ? 'Building preview…' : 'Preview'}
          </Button>
        )}
        {step === 'preview' && (
          <Button variant="contained" color="success" onClick={send} disabled={sending}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}>
            {sending ? 'Sending…' : `Send to ${selected.length} lender${selected.length !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </StackedDialog>
  );
}
